/**
 * State Style04 (ThermostatMode) - HVAC mode hero card
 */
import type { StateControlProps, StateLifecycle } from './stateStyleTypes'
import {
  getStateLifecycleColor,
  inferStateLifecycle,
} from './traitStyleConfig'
import './StateStyle04ThermostatMode.css'

const LIFECYCLE_LABEL: Record<StateLifecycle, string> = {
  live: 'LIVE',
  warn: 'WARN',
  error: 'ERROR',
  stale: 'STALE',
  offline: 'OFFLINE',
}

function normalizeModeToken(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
}

function getModeMeta(rawMode: unknown): { label: string; icon: string; chip: string } {
  const mode = normalizeModeToken(rawMode)
  if (mode === 'cool') return { label: 'Cool', icon: 'ac_unit', chip: 'Cooling' }
  if (mode === 'heat') return { label: 'Heat', icon: 'mode_heat', chip: 'Heating' }
  if (mode === 'dry') return { label: 'Dry', icon: 'water_drop', chip: 'Drying' }
  if (mode === 'fan-only' || mode === 'fanonly' || mode === 'fan') {
    return { label: 'Fan', icon: 'mode_fan', chip: 'Ventilation' }
  }
  if (mode === 'auto') return { label: 'Auto', icon: 'auto_awesome', chip: 'Auto' }
  if (mode === 'eco') return { label: 'Eco', icon: 'eco', chip: 'Energy Save' }
  if (!mode || mode === '—') return { label: 'Unknown', icon: 'help', chip: 'No Data' }
  return { label: String(rawMode), icon: 'tune', chip: 'Custom' }
}

export function StateStyle04ThermostatMode({
  stateKey,
  value,
  label,
  lifecycle,
  updatedAtLabel,
  thresholdEnabled,
  disabled = false,
}: StateControlProps) {
  const resolvedLifecycle =
    lifecycle ??
    (disabled
      ? 'offline'
      : inferStateLifecycle(stateKey, value, {
          thresholdEnabled,
        }))
  const lifecycleColor = getStateLifecycleColor(resolvedLifecycle)
  const lifecycleText = LIFECYCLE_LABEL[resolvedLifecycle]
  const updatedLabel = String(updatedAtLabel || '2m 前更新')
  const modeMeta = getModeMeta(value)
  const displayLabel = label || 'Thermostat mode'

  return (
    <div
      className="trait-state-style04-thermostat trait-stitch-glass"
      data-css-slot="stateContainer"
      data-css-slot-label="State Container"
      data-css-state={resolvedLifecycle}
    >
      <div
        className="trait-state-style04-thermostat__header"
        data-css-slot="stateLabel"
        data-css-slot-label="State Label"
        data-css-state={resolvedLifecycle}
      >
        {displayLabel}
      </div>

      <div className="trait-state-style04-thermostat__hero">
        <span
          className="material-symbols-outlined trait-state-style04-thermostat__icon"
          data-css-slot="stateIcon"
          data-css-slot-label="State Icon"
          data-css-state={resolvedLifecycle}
        >
          {modeMeta.icon}
        </span>
        <div
          className="trait-state-style04-thermostat__value"
          data-css-slot="stateValue"
          data-css-slot-label="State Value"
          data-css-state={resolvedLifecycle}
        >
          {modeMeta.label}
        </div>
        <span className="trait-state-style04-thermostat__chip">{modeMeta.chip}</span>
      </div>

      <div className="trait-state-style04-thermostat__meta">
        <span
          className="trait-state-style04-thermostat__badge"
          data-css-slot="stateBadge"
          data-css-slot-label="State Lifecycle Badge"
          data-css-state={resolvedLifecycle}
          style={{ '--state-lifecycle-color': lifecycleColor } as React.CSSProperties}
        >
          {lifecycleText}
        </span>
        <div
          className="trait-state-style04-thermostat__updated"
          data-css-slot="stateUpdatedAt"
          data-css-slot-label="State Updated Time"
          data-css-state={resolvedLifecycle}
        >
          <span className="material-symbols-outlined">schedule</span>
          {updatedLabel}
        </div>
      </div>
    </div>
  )
}
